import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useIsMcGrawHill } from '../mcgrawHill/mcgrawHill'; // mcgraw-hill
import { updateCurrentUserInfo } from '../services/auth';
import type { RootState } from '../store/store';

/**
 * `T` flips the account's saved theme between light and dark, outside text
 * fields and game rounds. For admins, and for every McGraw Hill member, who
 * demo both themes (ORA-1258).
 */
export const useThemeHotkey = (): void => {
  const currentUser = useSelector((state: RootState) => state.user.profile);
  const isMcGrawHill = useIsMcGrawHill();
  const canToggle = currentUser?.role === 'admin' || isMcGrawHill;
  const theme = currentUser?.theme;

  useEffect(() => {
    if (!canToggle) return;

    const handleThemeHotkey = (event: KeyboardEvent) => {
      const target = event.target;
      if (target instanceof HTMLElement) {
        const isFormElement =
          target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT';

        const isWithinEditableContainer =
          target.isContentEditable ||
          target.closest('[contenteditable]:not([contenteditable="false"])') !==
            null;

        if (isFormElement || isWithinEditableContainer) {
          return;
        }
      }

      const key = typeof event.key === 'string' ? event.key.toLowerCase() : '';
      if (key !== 't') return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (window.location.pathname.startsWith('/games/')) return;

      updateCurrentUserInfo({ theme: theme === 'dark' ? 'light' : 'dark' });
    };

    window.addEventListener('keydown', handleThemeHotkey);
    return () => {
      window.removeEventListener('keydown', handleThemeHotkey);
    };
  }, [canToggle, theme]);
};
